import {
  Component,
  FrameOption,
  Filter,
  ConfigState,
  PageSettingsMap,
  FrameCategory,
  Sticker,
  CustomTheme
} from '@renderer/types/configState'
import { create } from 'zustand'
import { filterUtils } from '@renderer/utils'
import { getBrand, type Brand } from '@renderer/utils/brandUtils'

const dreamboothCustomTheme = {
  newspaperTitle: 'DREAMBOOTH NEWS',
  newspaperDomain: 'dreamboothstudio.com',
  newspaperHeadline: 'BREAKING: {city} CITIZENS GATHER FOR HISTORIC PHOTO OPPORTUNITY',
  articles: [
    {
      heading: 'About Yogyakarta',
      paragraphs: [
        'Yogyakarta is known as the cultural capital of Java, preserving centuries-old Javanese traditions and royal heritage.',
        'The city is home to the Kraton (Sultan\'s Palace), a living museum where traditional Javanese culture continues to thrive.',
        'Yogyakarta is famous for its batik industry, with many local artisans creating intricate hand-drawn batik pieces recognized as UNESCO Intangible Cultural Heritage.'
      ]
    },
    {
      heading: 'Creativity Unleashed By Innovative Software',
      paragraphs: [
        'Dreambooth Studio empowers creators and event organizers with cutting-edge software that transforms ordinary moments into extraordinary memories. Our innovative platform combines intuitive design tools with powerful automation, enabling anyone to craft professional-grade photobooth experiences without technical complexity.',
        'The software revolutionizes how memories are captured, edited, and shared. Advanced features like real-time filters, customizable frames, and seamless cloud integration ensure every session delivers stunning results that exceed expectations.',
        'Built for scalability and reliability, Dreambooth Studio handles everything from intimate gatherings to large-scale events. Our platform adapts to any creative vision while maintaining the highest standards of quality and performance.'
      ]
    }
  ],
  checkoutSteps: [
    'Step 1: Review your order and select packages',
    'Step 2: Complete payment if required',
    'Step 3: Choose your filter style',
    'Step 4: Select your photo frame',
    'Step 5: Take your photos',
    'Step 6: Review and retake if needed',
    'Step 7: Get your photos!'
  ]
}

const bahelaCustomTheme = {
  ...dreamboothCustomTheme,
  newspaperTitle: 'BAHELA NEWS',
  newspaperDomain: 'bahela.id',
  articles: [
    dreamboothCustomTheme.articles[0],
    {
      heading: 'Bilik Foto Lawas',
      paragraphs: [
        'Bahela menghadirkan pengalaman bilik foto lawas yang autentik dengan kualitas HD di Jogja dan Solo.',
        'Abadikan momen berharga Anda dengan gaya vintage dan pelayanan profesional.'
      ]
    }
  ]
}

function getCustomThemeForBrand(brand: Brand): CustomTheme {
  return brand === 'bahela' ? bahelaCustomTheme as CustomTheme : dreamboothCustomTheme as CustomTheme
}

function getThumbnailForBrand(brand: Brand): string {
  if (brand === 'bahela') {
    return 'https://dreamboothidbucket.s3.ap-southeast-3.amazonaws.com/template/bahela-thumbnail.png'
  }
  return 'https://dreamboothidbucket.s3.ap-southeast-3.amazonaws.com/template/dreambooth-white.png'
}

/** Returns default config with brand-specific customTheme and thumbnail. Use when initializing or when no project config is loaded. */
export function getDefaultConfig(): ConfigState {
  const brand = getBrand()
  return {
    ...defaultConfig,
    customTheme: getCustomThemeForBrand(brand),
    thumbnail: getThumbnailForBrand(brand)
  }
}

export const defaultConfig: ConfigState = {
  title: 'Untitled',
  screenSize: {
    width: 1080,
    height: 1920
  },
  theme: {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF',
    backgroundColor: '#F8FAFC',
    fontFamily: 'Inter, system-ui, sans-serif',
    backgroundImageUrl: undefined,
    screensaverVideoUrl: undefined,
    idleTimeout: undefined
  },
  customTheme: dreamboothCustomTheme,
  thumbnail: `https://dreamboothidbucket.s3.ap-southeast-3.amazonaws.com/template/dreambooth-white.png`,
  eventId: undefined,
  eventGalleryEnabled: false,
  pages: {
    welcome: {
      name: 'Welcome',
      settings: {
        enabled: true,
        startWithPayment: false,
        customPath: null
      },
      components: []
    },
    filter: {
      name: 'Filter',
      settings: {
        enabled: true,
        filterTimeout: 0,
        filterIds: [
          '6790b001b653f3bd89ade06b',
          '6790b001b653f3bd89ade06c',
          '6790b001b653f3bd89ade06d',
          '6790b001b653f3bd89ade06e',
          '6790b001b653f3bd89ade06f'
        ],
        useLivePreview: true,
        applyAfterCapture: false
      }
    },
    frame: {
      name: 'Frame',
      settings: {
        enabled: true,
        frameTimeout: 0,
        frameIds: [],
        frameCategories: [],
        categoryModeEnabled: false
      }
    },
    checkout: {
      name: 'Checkout',
      settings: {
        enabled: true,
        checkoutTimeout: 60,
        promoEnabled: true,
        voucherEnabled: true,
        packages: [],
        promos: []
      }
    },
    payment: {
      name: 'Payment',
      settings: {
        enabled: true,
        paymentTimeout: 120
      }
    },
    capture: {
      name: 'Capture',
      settings: {
        enabled: true,
        prepTimeout: 30,
        captureCountdown: 2,
        captureCount: 8,
        mode: 'standard',
        gifEnabled: false,
        gifSpeed: 1,
        customGifFrame: '',
        recordingEnabled: false,
        recordingSpeed: 1,
        captureTimeout: 600, // 10 minutes default
        watermarkUrl: '',
        watermarkHorizontalAnchor: 'left',
        watermarkVerticalAnchor: 'top',
        watermarkHorizontalOffset: 0,
        watermarkVerticalOffset: 0
      }
    },
    record: {
      name: 'Record',
      settings: {
        enabled: false,
        prepTimeout: 30,
        recordingTimeout: 120,
        audioEnabled: false
      }
    },
    retake: {
      name: 'Retake',
      settings: {
        enabled: true,
        maxRetakeCount: 3,
        maxRetakeSession: 1,
        retakeTimeout: 20
      }
    },
    select: {
      name: 'Print Photos',
      settings: {
        enabled: true,
        selectionTimeout: 60
      }
    },
    result: {
      name: 'Final Result',
      settings: {
        enabled: true,
        resultTimeout: 60,
        askUserConsent: true,
        emailEnabled: true,
        reprintEnabled: false,
        reprintTimeout: 60,
        postSessionAiTimeout: 60,
        postSessionAiPaymentEnabled: false,
        postSessionAiPackages: []
      }
    },
    camera: {
      name: 'Camera',
      settings: {
        enabled: false,
        cameraTimeout: 30
      }
    },
    edit: {
      name: 'Edit',
      settings: {
        enabled: false,
        editTimeout: 0,
        stickerIds: []
      }
    }
  }
} as const

interface ConfigStoreState {
  config: ConfigState | null
  filters: Filter[]
  frames: FrameOption[]
  frameCategories: FrameCategory[]
  stickers: Sticker[]
  stickersLoaded: boolean // Track if all sticker images are loaded
  setConfig: (config: ConfigState) => void
  setFilters: (filters: Filter[]) => Promise<void>
  setFrames: (frames: FrameOption[]) => void
  /** Append a single frame (e.g. a voucher-linked custom frame) if not already
   *  present, caching just its assets. Used so a saved custom frame applies to
   *  any project even when the project's own frame list omits it. */
  ensureFrame: (frame: FrameOption) => void
  setFrameCategories: (categories: FrameCategory[]) => void
  setStickers: (stickers: Sticker[]) => void
  getPageSettings: <K extends keyof PageSettingsMap>(pageId: K) => PageSettingsMap[K] | undefined
  getFilter: (filterId: string) => Filter | undefined
  getFrame: (frameId: string, scale?: number) => FrameOption | undefined
  getSticker: (stickerId: string) => Sticker | undefined
  loadingProgress: {
    loaded: number
    total: number
  }
  resetLoadingProgress: () => void
  /** Evict a single URL from in-memory asset cache (and revoke blob URL if present) */
  evictAsset: (url: string) => void
}

// Cache for frame and component assets (in-memory for fast access)
const assetCache = new Map<string, string>()

// Helper function to load and cache an image with IndexedDB persistence
// Returns: blob URL if cached/fetched successfully, or original URL as fallback
// This function should never throw - it always returns a URL (either cached blob URL or original)
const loadAndCacheImage = async (url: string): Promise<string> => {
  // Check in-memory cache first (fast path)
  const cached = assetCache.get(url)
  if (cached) {
    console.debug('[ASSET_CACHE] Using in-memory cache:', url)
    return cached
  }

  // Check IndexedDB (skip empty/poisoned blobs)
  try {
    const { getCachedBlob, deleteCachedBlob } = await import('@renderer/utils/assetCacheDB')
    const blob = await getCachedBlob(url)
    if (blob && blob.size > 0) {
      const localUrl = URL.createObjectURL(blob)
      assetCache.set(url, localUrl)
      console.debug('[ASSET_CACHE] Using IndexedDB cache:', url)
      return localUrl
    }
    if (blob && blob.size === 0) {
      await deleteCachedBlob(url).catch(() => {})
      console.warn('[ASSET_CACHE] Evicted empty blob from IndexedDB:', url)
    }
  } catch (error) {
    console.warn('[ASSET_CACHE] Error loading from IndexedDB (will try network):', url, error)
  }

  // Fetch from network if not in cache
  try {
    const response = await fetch(url, {
      mode: 'cors',
      cache: 'no-cache'
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText || 'Failed to load image'}`)
    }
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('Empty blob from CORS fetch')
    }
    const localUrl = URL.createObjectURL(blob)

    // Store in IndexedDB for persistence
    try {
      const { setCachedBlob } = await import('@renderer/utils/assetCacheDB')
      await setCachedBlob(url, blob)
    } catch (error) {
      console.warn('[ASSET_CACHE] Failed to store blob in IndexedDB (memory cache will work):', url, error)
      // Continue anyway - memory cache will still work
    }

    // Store in memory cache for fast access
    assetCache.set(url, localUrl)
    console.debug('[ASSET_CACHE] Successfully fetched and cached:', url)
    return localUrl
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.warn('[ASSET_CACHE] CORS fetch failed, returning original URL for fallback:', url, errorMessage)
    // Return original URL so PrintPreview can try direct fetch (multi-level fallback)
    return url
  }
}

export const useConfigStore = create<ConfigStoreState>((set, get) => ({
  config: null,
  filters: [],
  frames: [],
  frameCategories: [],
  stickers: [],
  stickersLoaded: false,
  loadingProgress: { loaded: 0, total: 0 },

  resetLoadingProgress: (): void => set({ loadingProgress: { loaded: 0, total: 0 }, stickersLoaded: false }),

  evictAsset: (url: string): void => {
    const cached = assetCache.get(url)
    if (cached?.startsWith('blob:')) {
      URL.revokeObjectURL(cached)
    }
    assetCache.delete(url)
  },

  setConfig: (config: ConfigState): void => {
    if (!config.screenSizes && config.screenSize) {
      const w = config.screenSize.width
      const h = config.screenSize.height
      config.screenSizes = {
        portrait: { width: Math.min(w, h), height: Math.max(w, h) },
        landscape: { width: Math.max(w, h), height: Math.min(w, h) }
      }
    }
    const welcomePage = config.pages.welcome
    if (
      welcomePage &&
      !welcomePage.orientedComponents &&
      welcomePage.components &&
      welcomePage.components.length >= 0
    ) {
      welcomePage.orientedComponents = {
        portrait: JSON.parse(JSON.stringify(welcomePage.components)) as Component[],
        landscape: JSON.parse(JSON.stringify(welcomePage.components)) as Component[]
      }
    }

    if (welcomePage?.orientedComponents) {
      const oc = welcomePage.orientedComponents
      const legacy = welcomePage.components
      const p = oc.portrait
      const l = oc.landscape
      const pOk = Array.isArray(p) && p.length > 0
      const lOk = Array.isArray(l) && l.length > 0
      const legacyOk = Array.isArray(legacy) && legacy.length > 0
      const clone = (c: Component[]): Component[] => JSON.parse(JSON.stringify(c)) as Component[]
      if (!pOk && !lOk && legacyOk) {
        oc.portrait = clone(legacy)
        oc.landscape = clone(legacy)
      } else if (!pOk && legacyOk) {
        oc.portrait = clone(legacy)
      } else if (!lOk && legacyOk) {
        oc.landscape = clone(legacy)
      } else if (!pOk && lOk) {
        oc.portrait = clone(l)
      } else if (!lOk && pOk) {
        oc.landscape = clone(p)
      }
    }

    // Validate frame-based mode configuration
    const captureMode = config.pages.capture.settings.mode
    if (captureMode === 'frame-based') {
      // Frame page must be enabled for frame-based mode
      if (!config.pages.frame.settings.enabled) {
        console.warn(
          'Frame-based mode requires frame page to be enabled. Enabling frame page automatically.'
        )
        config.pages.frame.settings.enabled = true
      }

      // Capture count should be 0 (auto from frame) for frame-based mode
      if (config.pages.capture.settings.captureCount !== 0) {
        console.warn(
          'Frame-based mode should have captureCount set to 0 (auto from frame). Setting to 0.'
        )
        config.pages.capture.settings.captureCount = 0
      }

      // Warn if select page is enabled (should be disabled for frame-based)
      if (config.pages.select.settings.enabled) {
        console.warn(
          'Select page is enabled for frame-based mode. Consider disabling it as photos are auto-selected.'
        )
      }
    }

    const countHttpImages = (components?: Component[]): number => {
      if (!components) return 0
      return components.filter(
        (c) => c.type === 'image' && c.src && c.src.startsWith('http')
      ).length
    }

    let assetsToLoad = 0
    Object.values(config.pages).forEach((page) => {
      if (page.orientedComponents) {
        assetsToLoad += countHttpImages(page.orientedComponents.portrait)
        assetsToLoad += countHttpImages(page.orientedComponents.landscape)
      } else {
        assetsToLoad += countHttpImages(page.components)
      }
    })
    if (
      config.pages.capture.settings.customGifFrame &&
      config.pages.capture.settings.customGifFrame.startsWith('http')
    ) {
      assetsToLoad++
    }

    // Add watermark image to assets to load
    if (
      config.pages.capture.settings.watermarkUrl &&
      config.pages.capture.settings.watermarkUrl.startsWith('http')
    ) {
      assetsToLoad++
    }

    // Add filter preview image to assets to load if useLivePreview is false
    if (
      config.filterPreviewImageUrl &&
      config.filterPreviewImageUrl.startsWith('http') &&
      config.pages.filter.settings.useLivePreview === false
    ) {
      assetsToLoad++
    }

    // Add background image to assets to load
    if (
      config.theme.backgroundImageUrl &&
      config.theme.backgroundImageUrl.startsWith('http')
    ) {
      assetsToLoad++
    }

    set((state) => ({
      config,
      loadingProgress: {
        ...state.loadingProgress,
        total: state.loadingProgress.total + assetsToLoad
      }
    }))

    const processAsset = (
      updater: (state: ConfigStoreState, localUrl: string) => ConfigStoreState
    ) => {
      return (localUrl: string): void => {
        set((state) => {
          const updatedState = updater(state, localUrl)
          return {
            ...updatedState,
            loadingProgress: {
              ...updatedState.loadingProgress,
              loaded: updatedState.loadingProgress.loaded + 1
            }
          }
        })
      }
    }

    // Helper to increment loading progress even on error
    const incrementLoadedOnError = (assetUrl: string, error: unknown): void => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.warn('[ASSET_LOAD] Asset load failed (using fallback URL):', assetUrl, errorMessage)
      set((state) => ({
        ...state,
        loadingProgress: {
          ...state.loadingProgress,
          loaded: state.loadingProgress.loaded + 1
        }
      }))
    }

    type ImageListTarget =
      | { kind: 'components' }
      | { kind: 'oriented'; orientation: 'portrait' | 'landscape' }

    const queuePageImages = (
      pageId: keyof ConfigState['pages'],
      list: Component[] | undefined,
      target: ImageListTarget
    ): void => {
      if (!list) return
      list.forEach((component, index) => {
        if (component.type === 'image' && component.src && component.src.startsWith('http')) {
          const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
            const newConfig = JSON.parse(JSON.stringify(state.config)) as ConfigState
            const p = newConfig.pages[pageId]
            if (target.kind === 'components' && p.components) {
              p.components[index].src = localUrl
            } else if (target.kind === 'oriented' && p.orientedComponents) {
              p.orientedComponents[target.orientation][index].src = localUrl
            }
            return { ...state, config: newConfig }
          }
          loadAndCacheImage(component.src)
            .then(processAsset(updater))
            .catch((error) => {
              incrementLoadedOnError(component.src || '', error)
            })
        }
      })
    }

    ;(Object.keys(config.pages) as Array<keyof ConfigState['pages']>).forEach((pageId) => {
      const page = config.pages[pageId]
      if (page.orientedComponents) {
        queuePageImages(pageId, page.orientedComponents.portrait, {
          kind: 'oriented',
          orientation: 'portrait'
        })
        queuePageImages(pageId, page.orientedComponents.landscape, {
          kind: 'oriented',
          orientation: 'landscape'
        })
      } else {
        queuePageImages(pageId, page.components, { kind: 'components' })
      }
    })

    if (
      config.pages.capture.settings.customGifFrame &&
      config.pages.capture.settings.customGifFrame.startsWith('http')
    ) {
      const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
        const newConfig = JSON.parse(JSON.stringify(state.config))
        newConfig.pages.capture.settings.customGifFrame = localUrl
        return { ...state, config: newConfig }
      }
      loadAndCacheImage(config.pages.capture.settings.customGifFrame)
        .then(processAsset(updater))
        .catch((error) => {
          incrementLoadedOnError(config.pages.capture.settings.customGifFrame || '', error)
        })
    }

    // Cache watermark image
    if (
      config.pages.capture.settings.watermarkUrl &&
      config.pages.capture.settings.watermarkUrl.startsWith('http')
    ) {
      const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
        const newConfig = JSON.parse(JSON.stringify(state.config))
        newConfig.pages.capture.settings.watermarkUrl = localUrl
        return { ...state, config: newConfig }
      }
      loadAndCacheImage(config.pages.capture.settings.watermarkUrl)
        .then(processAsset(updater))
        .catch((error) => {
          incrementLoadedOnError(config.pages.capture.settings.watermarkUrl || '', error)
        })
    }

    // Cache filter preview image if useLivePreview is false
    if (
      config.filterPreviewImageUrl &&
      config.filterPreviewImageUrl.startsWith('http') &&
      config.pages.filter.settings.useLivePreview === false
    ) {
      const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
        const newConfig = JSON.parse(JSON.stringify(state.config))
        newConfig.filterPreviewImageUrl = localUrl
        return { ...state, config: newConfig }
      }
      loadAndCacheImage(config.filterPreviewImageUrl)
        .then(processAsset(updater))
        .catch((error) => {
          incrementLoadedOnError(config.filterPreviewImageUrl || '', error)
        })
    }

    // Cache background image
    if (
      config.theme.backgroundImageUrl &&
      config.theme.backgroundImageUrl.startsWith('http')
    ) {
      const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
        const newConfig = JSON.parse(JSON.stringify(state.config))
        newConfig.theme.backgroundImageUrl = localUrl
        return { ...state, config: newConfig }
      }
      loadAndCacheImage(config.theme.backgroundImageUrl)
        .then(processAsset(updater))
        .catch((error) => {
          incrementLoadedOnError(config.theme.backgroundImageUrl || '', error)
        })
    }

    // Cache screensaver video
    if (
      config.theme.screensaverVideoUrl &&
      config.theme.screensaverVideoUrl.startsWith('http')
    ) {
      const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
        const newConfig = JSON.parse(JSON.stringify(state.config))
        newConfig.theme.screensaverVideoUrl = localUrl
        return { ...state, config: newConfig }
      }
      loadAndCacheImage(config.theme.screensaverVideoUrl)
        .then(processAsset(updater))
        .catch((error) => {
          incrementLoadedOnError(config.theme.screensaverVideoUrl || '', error)
        })
    }
  },

  setFilters: async (filters: Filter[]): Promise<void> => {
    const state = get()
    const useLivePreview = state.config?.pages.filter.settings.useLivePreview ?? true
    const neutralPhotoUrl = state.config?.filterPreviewImageUrl

    // If useLivePreview is false and we have a neutral photo, generate thumbnails
    if (!useLivePreview && neutralPhotoUrl && neutralPhotoUrl.startsWith('http')) {
      try {
        const response = await fetch(neutralPhotoUrl)
        const neutralBlob = await response.blob()
        const neutralImageBitmap = await createImageBitmap(neutralBlob)

        // Generate thumbnail for each filter
        const filtersWithThumbnails = await Promise.all(
          filters.map(async (filter) => {
            try {
              // Create a canvas for the thumbnail
              const canvas = document.createElement('canvas')
              canvas.width = 144
              canvas.height = 144
              const ctx = canvas.getContext('2d', { willReadFrequently: true })

              if (!ctx) return filter

              // Create a temporary canvas to process the filter
              const tempCanvas = document.createElement('canvas')
              tempCanvas.width = neutralImageBitmap.width
              tempCanvas.height = neutralImageBitmap.height
              const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true })

              if (!tempCtx) return filter

              // Draw the neutral image
              tempCtx.drawImage(neutralImageBitmap, 0, 0)

              // Apply filter adjustments
              if (filter.adjustments) {
                filterUtils.applyAdjustments(tempCtx, filter.adjustments)
              }

              // Scale and center the filtered image to thumbnail size
              const scale = Math.max(
                canvas.width / tempCanvas.width,
                canvas.height / tempCanvas.height
              )
              const x = (canvas.width - tempCanvas.width * scale) / 2
              const y = (canvas.height - tempCanvas.height * scale) / 2

              ctx.drawImage(tempCanvas, x, y, tempCanvas.width * scale, tempCanvas.height * scale)

              // Convert to blob URL
              const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((blob) => {
                  if (blob) resolve(blob)
                  else reject(new Error('Failed to create blob'))
                }, 'image/png')
              })

              const thumbnailUrl = URL.createObjectURL(blob)

              return { ...filter, cachedThumbnail: thumbnailUrl }
            } catch (error) {
              console.error('Error generating thumbnail for filter:', filter._id, error)
              return filter
            }
          })
        )

        neutralImageBitmap.close()
        set(() => ({ filters: filtersWithThumbnails }))
      } catch (error) {
        console.error('Error loading neutral photo for thumbnails:', error)
        set(() => ({ filters }))
      }
    } else {
      set(() => ({ filters }))
    }
  },

  setFrames: (frames: FrameOption[]): void => {
    let assetsToLoad = 0
    frames.forEach((frame) => {
      if (frame.thumbnail && frame.thumbnail.startsWith('http')) {
        assetsToLoad++
      }
      frame.drawParams.images.forEach((img) => {
        if (img.src && img.src.startsWith('http')) {
          assetsToLoad++
        }
      })
    })

    set((state) => ({
      frames,
      loadingProgress: {
        ...state.loadingProgress,
        total: state.loadingProgress.total + assetsToLoad
      }
    }))

    const processAsset = (
      updater: (state: ConfigStoreState, localUrl: string) => ConfigStoreState
    ) => {
      return (localUrl: string): void => {
        set((state) => {
          const updatedState = updater(state, localUrl)
          return {
            ...updatedState,
            loadingProgress: {
              ...updatedState.loadingProgress,
              loaded: updatedState.loadingProgress.loaded + 1
            }
          }
        })
      }
    }

    // Helper to increment loading progress even on error
    const incrementLoadedOnError = (assetUrl: string, error: unknown): void => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.warn('[ASSET_LOAD] Asset load failed (using fallback URL):', assetUrl, errorMessage)
      set((state) => ({
        ...state,
        loadingProgress: {
          ...state.loadingProgress,
          loaded: state.loadingProgress.loaded + 1
        }
      }))
    }

    frames.forEach((frame) => {
      if (frame.thumbnail && frame.thumbnail.startsWith('http')) {
        const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
          const newFrames = JSON.parse(JSON.stringify(state.frames))
          const frameToUpdate = newFrames.find((f) => f._id === frame._id)
          if (frameToUpdate) frameToUpdate.thumbnail = localUrl
          return { ...state, frames: newFrames }
        }
        loadAndCacheImage(frame.thumbnail)
          .then(processAsset(updater))
          .catch((error) => {
            incrementLoadedOnError(frame.thumbnail!, error)
          })
      }

      frame.drawParams.images.forEach((img, imgIndex) => {
        if (img.src && img.src.startsWith('http')) {
          const originalUrl = img.src
          const updater = (state: ConfigStoreState, localUrl: string): ConfigStoreState => {
            const newFrames = JSON.parse(JSON.stringify(state.frames))
            const frameToUpdate = newFrames.find((f) => f._id === frame._id)
            if (frameToUpdate) {
              frameToUpdate.drawParams.images[imgIndex].src = localUrl
              frameToUpdate.drawParams.images[imgIndex].originalSrc = originalUrl
            }
            return { ...state, frames: newFrames }
          }
          loadAndCacheImage(originalUrl)
            .then(processAsset(updater))
            .catch((error) => {
              incrementLoadedOnError(originalUrl, error)
            })
        }
      })
    })
  },

  ensureFrame: (frame: FrameOption): void => {
    const exists = get().frames.some((f) => f._id === frame._id)
    if (exists) return

    let assetsToLoad = 0
    if (frame.thumbnail && frame.thumbnail.startsWith('http')) assetsToLoad++
    frame.drawParams.images.forEach((img) => {
      if (img.src && img.src.startsWith('http')) assetsToLoad++
    })

    set((state) => ({
      frames: [...state.frames, frame],
      loadingProgress: {
        ...state.loadingProgress,
        total: state.loadingProgress.total + assetsToLoad
      }
    }))

    const incrementLoaded = (): void =>
      set((state) => ({
        loadingProgress: {
          ...state.loadingProgress,
          loaded: state.loadingProgress.loaded + 1
        }
      }))

    if (frame.thumbnail && frame.thumbnail.startsWith('http')) {
      const thumbnailUrl = frame.thumbnail
      loadAndCacheImage(thumbnailUrl)
        .then((localUrl) => {
          set((state) => {
            const newFrames = JSON.parse(JSON.stringify(state.frames))
            const frameToUpdate = newFrames.find((f) => f._id === frame._id)
            if (frameToUpdate) frameToUpdate.thumbnail = localUrl
            return { frames: newFrames }
          })
          incrementLoaded()
        })
        .catch((error) => {
          console.warn('[ASSET_LOAD] ensureFrame thumbnail failed:', thumbnailUrl, error)
          incrementLoaded()
        })
    }

    frame.drawParams.images.forEach((img, imgIndex) => {
      if (img.src && img.src.startsWith('http')) {
        const originalUrl = img.src
        loadAndCacheImage(originalUrl)
          .then((localUrl) => {
            set((state) => {
              const newFrames = JSON.parse(JSON.stringify(state.frames))
              const frameToUpdate = newFrames.find((f) => f._id === frame._id)
              if (frameToUpdate) {
                frameToUpdate.drawParams.images[imgIndex].src = localUrl
                frameToUpdate.drawParams.images[imgIndex].originalSrc = originalUrl
              }
              return { frames: newFrames }
            })
            incrementLoaded()
          })
          .catch((error) => {
            console.warn('[ASSET_LOAD] ensureFrame image failed:', originalUrl, error)
            incrementLoaded()
          })
      }
    })
  },

  setFrameCategories: (frameCategories: FrameCategory[]): void => set(() => ({ frameCategories })),

  getPageSettings: <K extends keyof PageSettingsMap>(pageId: K): PageSettingsMap[K] | undefined => {
    const config = get().config
    return config?.pages[pageId]?.settings
  },

  getFilter: (filterId: string): Filter | undefined => {
    return get().filters.find((filter) => filter._id === filterId)
  },

  getFrame: (frameId: string, scale: number = 1): FrameOption | undefined => {
    const frame = get().frames.find((f) => f._id === frameId)
    if (!frame) return undefined

    if (scale === 1) return frame // No scaling needed

    // Create a deep copy to avoid modifying the original cached frame
    const scaledFrame: FrameOption = JSON.parse(JSON.stringify(frame))

    scaledFrame.drawParams.canvasWidth *= scale
    scaledFrame.drawParams.canvasHeight *= scale

    scaledFrame.drawParams.images = scaledFrame.drawParams.images.map((img) => ({
      ...img,
      top: img.top * scale,
      left: img.left * scale,
      width: img.width * scale,
      height: img.height * scale
    }))

    return scaledFrame
  },

  getSticker: (stickerId: string): Sticker | undefined => {
    return get().stickers.find((s) => s._id === stickerId)
  },

  setStickers: (stickers: Sticker[]): void => {
    // Reset loaded state when setting new stickers
    set({ stickers, stickersLoaded: false })

    let assetsToLoad = 0
    const stickersToLoad: Array<{ sticker: Sticker; urlKey: 'url' | 'thumbnail' }> = []

    stickers.forEach((sticker) => {
      if (sticker.url && sticker.url.startsWith('http')) {
        assetsToLoad++
        stickersToLoad.push({ sticker, urlKey: 'url' })
      }
      if (sticker.thumbnail && sticker.thumbnail.startsWith('http') && sticker.thumbnail !== sticker.url) {
        assetsToLoad++
        stickersToLoad.push({ sticker, urlKey: 'thumbnail' })
      }
    })

    set((state) => ({
      loadingProgress: {
        ...state.loadingProgress,
        total: state.loadingProgress.total + assetsToLoad
      }
    }))

    if (assetsToLoad === 0) {
      // No assets to load, mark as loaded immediately
      set({ stickersLoaded: true })
      return
    }

    let loadedCount = 0
    const totalToLoad = assetsToLoad

    const processAsset = (
      updater: (state: ConfigStoreState, localUrl: string) => ConfigStoreState
    ) => {
      return (localUrl: string): void => {
        set((state) => {
          const updatedState = updater(state, localUrl)
          loadedCount++
          const allLoaded = loadedCount >= totalToLoad
          return {
            ...updatedState,
            stickersLoaded: allLoaded,
            loadingProgress: {
              ...updatedState.loadingProgress,
              loaded: updatedState.loadingProgress.loaded + 1
            }
          }
        })
      }
    }

    // Helper to increment loading progress even on error
    const incrementLoadedOnError = (assetUrl: string, error: unknown): void => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.warn('[ASSET_LOAD] Sticker asset load failed (using fallback URL):', assetUrl, errorMessage)
      set((state) => {
        loadedCount++
        const allLoaded = loadedCount >= totalToLoad
        return {
          ...state,
          stickersLoaded: allLoaded,
          loadingProgress: {
            ...state.loadingProgress,
            loaded: state.loadingProgress.loaded + 1
          }
        }
      })
    }

    // Load all sticker assets in parallel for better performance
    stickersToLoad.forEach(({ sticker, urlKey }) => {
      const url = sticker[urlKey]
      if (!url) return

      loadAndCacheImage(url)
        .then(
          processAsset((state, localUrl) => {
            // If thumbnail is the same as url, update both when url is cached
            const updatedStickers = state.stickers.map((s) => {
              if (s._id === sticker._id) {
                const updated = { ...s, [urlKey]: localUrl }
                // If thumbnail equals url, update thumbnail too when url is cached
                if (urlKey === 'url' && s.thumbnail === s.url) {
                  updated.thumbnail = localUrl
                }
                return updated
              }
              return s
            })
            return {
              ...state,
              stickers: updatedStickers
            }
          })
        )
        .catch((error) => {
          incrementLoadedOnError(url, error)
        })
    })
  }
}))
